import { addCellAfter, getCellCoordinateInPlan, getTablePlan, insertNewCellAtCoordinate } from './table';

/**
 * Mutates table so that all cells between:
 * - startRowIndex and endRowIndex,
 * - startColIndex and endColIndex,
 * That were of dimensions greater than 1*1 get broken down into cells of dimension 1*1.
 * If a cell is overlapping on this zone, its part within the zone gets broken down, its part ouf of the zone does not.
 * @param table: HTMLTableElement,
 * @param startRowIndex: number,
 * @param endRowIndex: number,
 * @param startColIndex: number,
 * @param endColIndex: number,
 */
export function normalizeTableZone(
  table: HTMLTableElement,
  startRowIndex: number,
  endRowIndex: number,
  startColIndex: number,
  endColIndex: number,
) {
  const document = table.ownerDocument;
  if (!document) {
    return;
  }
  // Split cells spreading beyond endRow.
  splitCellsOverlappingVertically(table, document, startColIndex, endColIndex, endRowIndex);
  // Split cells spreading beyond endCol.
  splitCellsOverlappingHorizontally(table, document, startRowIndex, endRowIndex, endColIndex);
  const tablePlan = getTablePlan(table);

  for (let rowIndex = endRowIndex; rowIndex >= startRowIndex; rowIndex--) {
    const rowPlan = tablePlan[rowIndex];
    if (!rowPlan) {
      continue;
    }
    for (let colIndex = endColIndex; colIndex >= startColIndex; colIndex--) {
      let cell: HTMLTableCellElement | undefined = rowPlan[colIndex];
      if (!cell) {
        continue;
      }
      const { rowSpan, colSpan } = cell;
      const coordinate = getCellCoordinateInPlan(cell, tablePlan);
      if (!coordinate) {
        return; // Cannot get there since cell comes from tablePlan, but typescript doesn't know that.
      }
      if (rowSpan > 1 && coordinate.row !== rowIndex) {
        // Remove this cell from current row by decrementing its rowspan.
        cell.setAttribute('rowspan', (rowSpan - 1).toString());
        // Add new cell to current row. It spreads on one row only, but on the same number of columns as the original.
        cell =
          insertNewCellAtCoordinate(table, document, { row: rowIndex, column: colIndex }, 1, colSpan) ||
          undefined;
        if (!cell) {
          return; // Cannot get there since we know table has a row at rowIndex.
        }
        // Update tablePlan.
        for (let i = 0; i < colSpan; i++) {
          rowPlan[coordinate.column + i] = cell;
        }
      }
      if (colSpan > 1) {
        // Remove this cell from current col by decrementing its colspan.
        cell.setAttribute('colspan', (colSpan - 1).toString());
        // Add new cell of dimension 1*1 after orinal cell to compensate.
        addCellAfter(table, cell);
      }
    }
  }
}

/**
 * Split all cells between startColIndex and endColIndex that spread vertically beyond endRowIndex in two cells:
 * - One spreading from its original starting row up to endRowIndex,
 * - One spreading from endRowIndex + 1 up to its original ending row,
 * Horizontal dimension remain the same as original cell for the two new cells.
 */
function splitCellsOverlappingVertically(
  table: HTMLTableElement,
  document: Document,
  startColIndex: number,
  endColIndex: number,
  endRowIndex: number,
) {
  const tablePlan = getTablePlan(table);
  const rowPlan = tablePlan[endRowIndex];
  // Nothing to do if table has less rows than endRowIndex.
  if (!rowPlan) {
    return;
  }
  let colIndex = startColIndex;
  while (colIndex <= endColIndex) {
    const cell = rowPlan[colIndex];
    // Nothing to do if table has less columns than colIndex.
    if (!cell) {
      break;
    }
    const { rowSpan, colSpan } = cell;
    const coordinate = getCellCoordinateInPlan(cell, tablePlan);
    if (!coordinate) {
      return; // Cannot get there since cell comes from tablePlan, but typescript doesn't know that.
    }
    if (coordinate.row + cell.rowSpan - 1 > endRowIndex) {
      const extraRows = coordinate.row + cell.rowSpan - 1 - endRowIndex;
      // Make original cell smaller so that is does not overflow.
      cell.setAttribute('rowSpan', (rowSpan - extraRows).toString());
      // Add new cell at endRowIndex + 1 of same width and of the remaining height.
      const newCell = insertNewCellAtCoordinate(
        table,
        document,
        { row: endRowIndex + 1, column: coordinate.column },
        extraRows,
        colSpan,
      );
      if (newCell) {
        // Arbitrary put cell's content outside normalize zone,
        // (since this function is used for pasting and that inner content might be erased when pasting).
        newCell.innerHTML = cell.innerHTML;
        cell.innerHTML = '';
        // Retrieve original style.
        const style = cell.getAttribute('style');
        if (style) {
          newCell.setAttribute('style', style);
        }
      }
    }
    colIndex = coordinate.column + colSpan;
  }
}

/**
 * Split all cells between startRowIndex and endRowIndex that spread horizontally beyond endColIndex in two cells:
 * - One spreading from its original starting column up to endColIndex,
 * - One spreading from endColIndex + 1 up to its original ending column,
 * Vertical dimension remain the same as original cell for the two new cells.
 */
function splitCellsOverlappingHorizontally(
  table: HTMLTableElement,
  document: Document,
  startRowIndex: number,
  endRowIndex: number,
  endColIndex: number,
) {
  const tablePlan = getTablePlan(table);
  let rowIndex = startRowIndex;
  while (rowIndex <= endRowIndex) {
    const rowPlan = tablePlan[rowIndex];
    // Nothing to do if table has less rows than endRowIndex.
    if (!rowPlan) {
      break;
    }
    const cell = rowPlan[endColIndex];
    // Nothing to do if current row has less columns than colIndex.
    if (!cell) {
      break;
    }
    const { rowSpan, colSpan } = cell;
    const coordinate = getCellCoordinateInPlan(cell, tablePlan);
    if (!coordinate) {
      return; // Cannot get there since cell comes from tablePlan, but typescript doesn't know that.
    }
    if (coordinate.column + colSpan - 1 > endColIndex) {
      const extraCols = coordinate.column + colSpan - 1 - endColIndex;
      // Make original cell smaller so that is does not overflow.
      cell.setAttribute('colSpan', (colSpan - extraCols).toString());
      // Add new cell at endColIndex + 1 of same height and of the remaining width.
      const newCell = insertNewCellAtCoordinate(
        table,
        document,
        { row: coordinate.row, column: endColIndex + 1 },
        rowSpan,
        extraCols,
      );
      if (newCell) {
        // Arbitrary put cell's content outside normalize zone,
        // (since this function is used for pasting and that inner content might be erased when pasting).
        newCell.innerHTML = cell.innerHTML;
        cell.innerHTML = '';
        // Retrieve original style.
        const style = cell.getAttribute('style');
        if (style) {
          newCell.setAttribute('style', style);
        }
      }
    }
    rowIndex = coordinate.row + rowSpan;
  }
}
